<template>
  <Button
    icon="pi pi-ellipsis-v"
    text
    rounded
    severity="secondary"
    size="small"
    :aria-label="t('authoredDocuments.actions.menu_label')"
    aria-haspopup="true"
    :aria-controls="menuId"
    :disabled="busy"
    :loading="busy"
    data-testid="authored-document-actions"
    @click="menu?.toggle($event)"
  />
  <Menu :id="menuId" ref="menu" :model="items" popup />
</template>

<script setup lang="ts">
/**
 * A document's own actions — rename, draft ↔ final, delete (MYAC-1375) — and, on
 * the library row, its three exports (MYAC-1667).
 *
 * One menu for the library row and the document page, so the two cannot drift into
 * offering different verbs. It only says what was chosen; the caller owns the dialogs
 * and the command (`useAuthoredDocumentActions`), because the row and the page react
 * differently to a deletion (the row simply disappears; the page has to leave).
 *
 * The status entry names the move, not the state: a final document offers "Back to
 * draft", anything else offers "Mark as final". `archived` exists in the backend's
 * vocabulary but nothing here moves a document into it.
 *
 * The exports are opt-in (`exportable`): the document page already has its Export
 * button beside this menu, and offering them twice side by side reads as two
 * different things. On the row this menu is the only way out, which is why the
 * quality manager of the 27/10 call never found the export and copy-pasted instead.
 */
import { computed, ref, useId } from 'vue'
import { useI18n } from 'vue-i18n'
import Button from 'primevue/button'
import Menu from 'primevue/menu'
import type { AuthoredDocumentStatus } from '@/composables/useAuthoredDocumentActions'
import {
  AUTHORED_DOCUMENT_EXPORTS,
  type AuthoredDocumentExportKind,
} from '@/composables/authoredDocumentExportOptions'

const props = withDefaults(
  defineProps<{
    /** The document's current `lifecycle_state` (an open string on the wire). */
    state?: string | null
    /** An action on this document is in flight. */
    busy?: boolean
    /** Offer the three exports — where no Export button stands beside the menu. */
    exportable?: boolean
  }>(),
  { state: 'draft', busy: false, exportable: false },
)

const emit = defineEmits<{
  rename: []
  'set-status': [status: AuthoredDocumentStatus]
  delete: []
  export: [kind: AuthoredDocumentExportKind]
}>()

const { t } = useI18n()
const menu = ref<InstanceType<typeof Menu> | null>(null)
const menuId = `authored-document-actions-${useId()}`

/** The reader's own entries, from the list both menus read. */
const exportItems = computed(() =>
  props.exportable
    ? [
        ...AUTHORED_DOCUMENT_EXPORTS.map((option) => ({
          label: t(option.labelKey),
          icon: option.icon,
          command: () => emit('export', option.kind),
        })),
        { separator: true },
      ]
    : [],
)

const items = computed(() => [
  ...exportItems.value,
  {
    label: t('authoredDocuments.actions.rename'),
    icon: 'pi pi-pencil',
    command: () => emit('rename'),
  },
  props.state === 'final'
    ? {
        label: t('authoredDocuments.actions.mark_draft'),
        icon: 'pi pi-undo',
        command: () => emit('set-status', 'draft'),
      }
    : {
        label: t('authoredDocuments.actions.mark_final'),
        icon: 'pi pi-check-circle',
        command: () => emit('set-status', 'final'),
      },
  { separator: true },
  {
    label: t('authoredDocuments.actions.delete'),
    icon: 'pi pi-trash',
    class: 'authored-document-delete-item',
    command: () => emit('delete'),
  },
])
</script>

<!-- Not scoped: a popup Menu is teleported to <body>, outside this component's
     scope, so a scoped rule never reaches its items. The class name is unique. -->
<style>
.authored-document-delete-item .p-menu-item-label,
.authored-document-delete-item .p-menu-item-icon {
  color: var(--p-red-600);
}
</style>
